import { User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { GetReservationsForDoctor } from '@virtual-care/client';
import {
  displayName,
  formatReservationDate,
  formatReservationTime,
} from '@virtual-care/isomorphic';
import { ReservationStatus } from '@virtual-care/ui';

export function AppointmentCard({
  reservation,
}: {
  reservation: GetReservationsForDoctor['records'][number];
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const formattedDate = formatReservationDate(reservation);
  const formattedTime = formatReservationTime(reservation);

  const doctorName = reservation.doctor
    ? `${reservation.doctor.firstName} ${reservation.doctor.lastName}`
    : undefined;

  return (
    <div
      className="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
      onClick={() => navigate(`/appointments/${reservation.id}`)}
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-900">
            <User className="h-4 text-white" />
          </div>
          <div className="flex flex-col items-start gap-1">
            <span className="font-bold text-black">
              {t('cards.appointment.prefix')} {reservation.reservationNumber}#
            </span>
            <ReservationStatus.Root status={reservation.status}>
              <ReservationStatus.Indicator />
              <ReservationStatus.Label render={(meta) => t(meta.key)} />
            </ReservationStatus.Root>
          </div>
        </div>
      </div>
      <div className="space-y-3 text-sm">
        <p>
          <span className="text-muted-foreground">
            {t('cards.appointment.date')}:
          </span>{' '}
          <span className="font-semibold">{formattedDate}</span>
        </p>
        <p>
          <span className="text-muted-foreground">
            {t('cards.appointment.time')}:
          </span>{' '}
          <span className="font-semibold">{formattedTime}</span>
        </p>

        {!doctorName && (
          <p>
            <span className="text-muted-foreground">
              {t('cards.appointment.patientName')}:
            </span>{' '}
            <span className="font-semibold">
              {displayName(reservation.user)}
            </span>
          </p>
        )}
        {doctorName && (
          <p>
            <span className="text-muted-foreground">
              {t('cards.appointment.doctorName')}:
            </span>{' '}
            <span className="font-semibold">{doctorName}</span>
          </p>
        )}
      </div>
    </div>
  );
}
